import React, { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
  Stack,
  alpha,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, Link as RouterLink } from "react-router-dom";
import { syncGoogleCalendar } from "../../api/client";

const KNOWN_REASONS = new Set([
  "access_denied",
  "policy",
  "scope",
  "invalid_request",
  "google_unavailable",
  "invalid_state",
  "config",
  "token",
  "user_mismatch",
  "unknown",
]);

function resolveReason(params) {
  const raw = String(params.get("reason") || "").trim().toLowerCase();
  if (KNOWN_REASONS.has(raw)) return raw;
  // Compat: callbacks antigos só com ?error=1
  if (params.get("error") === "1") return "unknown";
  return null;
}

export default function GoogleCalendarCallbackPage() {
  const { t } = useTranslation("profile");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const connected = params.get("connected") === "1";
  const reason = resolveReason(params);
  const hasError = Boolean(reason) && !connected;

  const copy = useMemo(() => {
    if (connected) {
      return {
        tone: "success",
        title: t("googleCalendar.callback.successTitle"),
        body: t("googleCalendar.callback.successBody"),
        howTo: null,
      };
    }
    if (!hasError) {
      return {
        tone: "loading",
        title: t("googleCalendar.callback.loadingTitle"),
        body: t("googleCalendar.callback.loadingBody"),
        howTo: null,
      };
    }
    const key = KNOWN_REASONS.has(reason) ? reason : "unknown";
    return {
      tone: "error",
      title: t(`googleCalendar.callback.reasons.${key}.title`),
      body: t(`googleCalendar.callback.reasons.${key}.body`),
      howTo: t(`googleCalendar.callback.reasons.${key}.howTo`),
    };
  }, [connected, hasError, reason, t]);

  useEffect(() => {
    if (connected) {
      syncGoogleCalendar({ forceFullSync: true }).catch(() => {});
    }
    if (connected || hasError) {
      const timer = setTimeout(() => navigate("/profile", { replace: true }), connected ? 3200 : 12000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [navigate, connected, hasError]);

  const Icon =
    copy.tone === "success" ? CheckCircleOutlineIcon : copy.tone === "error" ? ErrorOutlineIcon : EventIcon;
  const iconColor =
    copy.tone === "success" ? "success.main" : copy.tone === "error" ? "warning.main" : "primary.main";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, sm: 3 },
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? theme.palette.background.default : alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 480,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2.25} alignItems="flex-start">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: (theme) =>
                alpha(
                  copy.tone === "success"
                    ? theme.palette.success.main
                    : copy.tone === "error"
                      ? theme.palette.warning.main
                      : theme.palette.primary.main,
                  0.12
                ),
              color: iconColor,
            }}
          >
            {copy.tone === "loading" ? <CircularProgress size={22} /> : <Icon />}
          </Box>

          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {copy.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {copy.body}
            </Typography>
            {copy.howTo ? (
              <Typography variant="body2" color="text.primary" sx={{ mt: 1.5, lineHeight: 1.6, fontWeight: 500 }}>
                {copy.howTo}
              </Typography>
            ) : null}
          </Box>

          {hasError || connected ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%", pt: 0.5 }}>
              <Button
                component={RouterLink}
                to="/profile"
                variant="contained"
                fullWidth
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                {t("googleCalendar.callback.backToProfile")}
              </Button>
              {hasError ? (
                <Button
                  component={RouterLink}
                  to="/profile"
                  variant="outlined"
                  fullWidth
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {t("googleCalendar.callback.tryAgain")}
                </Button>
              ) : null}
            </Stack>
          ) : null}

          {connected ? (
            <Typography variant="caption" color="text.secondary">
              {t("googleCalendar.callback.redirecting")}
            </Typography>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
